import { useState, useEffect } from 'react';
import { Toast } from 'antd-mobile';
import { t } from '../../../../i18n';
import { getAccessCodesByOrderId, getPendingAccessCodesForDriver, AccessCodeWithStatsType } from '../../graphql/queries/accessCodeQueries';
import { markValidationPopupShown, submitFeedback } from '../../graphql/mutations/accessCodeMutations';
import { getUserInfo } from '../../../../utils/auth';
import { AccessCode } from '../components/AccessInfoDisplay';
import { getCurrentLocation } from '../../../../utils/locationUtils';
import { ApolloError } from '@apollo/client';
import { useAccessCodeFeature } from './useAccessCodeFeature';

/**
 * 将后端返回的AccessCodeWithStatsType转换为前端AccessCode格式
 */
const formatAccessCode = (code: {
    id: number;
    codeType: string;
    accessCode: string | null;
    status: number | null;
    accessCodeCreatedAt: number | null;
    accessCodeUpdatedAt: number | null;
    validFeedbackCount: number;
    invalidFeedbackCount: number;
    addressId: number;
    latitude: number | null;
    longitude: number | null;
    currentDriverCreatedAt?: number | null;
    currentDriverUpdatedAt?: number | null;
}): AccessCode => ({
    id: code.id,
    type: code.codeType,
    code: code.accessCode || '',
    status: code.status,
    createdAt: code.accessCodeCreatedAt ?? undefined,
    updatedAt: code.accessCodeUpdatedAt ?? undefined,
    validFeedbackCount: code.validFeedbackCount,
    invalidFeedbackCount: code.invalidFeedbackCount,
    orderAddressId: code.addressId,
    latitude: code.latitude ?? undefined,
    longitude: code.longitude ?? undefined,
    currentDriverCreatedAt: code.currentDriverCreatedAt ?? undefined,
    currentDriverUpdatedAt: code.currentDriverUpdatedAt ?? undefined
});

// 请求去重：避免同一orderId的并发请求（统一缓存原始数据）
const pendingRequests = new Map<number, Promise<AccessCodeWithStatsType[]>>();
const requestCache = new Map<number, { data: AccessCodeWithStatsType[]; timestamp: number }>();
const CACHE_DURATION = 5000; // 缓存5秒

/**
 * 清除指定orderId的缓存（在更新操作后调用）
 */
export const clearAccessCodesCache = (orderId: number): void => {
    requestCache.delete(orderId);
    pendingRequests.delete(orderId);
};

/**
 * 清除所有缓存（用于页面切换等场景）
 */
export const clearAllAccessCodesCache = (): void => {
    requestCache.clear();
    pendingRequests.clear();
};

/**
 * 带去重和缓存的获取门禁码原始数据函数（统一缓存原始数据）
 * 返回原始数据，包含 currentUserFeedback 字段
 */
export const getAccessCodesRawWithDedup = async (orderId: number): Promise<AccessCodeWithStatsType[]> => {
    // 1. 检查缓存
    const cached = requestCache.get(orderId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return cached.data;
    }

    // 2. 检查是否有正在进行的请求
    const pending = pendingRequests.get(orderId);
    if (pending) {
        return pending;
    }

    // 3. 创建新请求
    const request = (async () => {
        try {
            const codes = await getAccessCodesByOrderId(orderId);
            
            // 更新缓存（缓存原始数据）
            requestCache.set(orderId, {
                data: codes,
                timestamp: Date.now()
            });
            
            return codes;
        } catch (error) {
            // 包装内部错误为网络异常（可能是电话打断或弱网环境）
            if (error instanceof ApolloError) {
                // 检查是否是网络错误
                if (error.networkError) {
                    // 已经是网络错误，直接抛出
                    throw error;
                }
                
                // 检查是否是内部错误（可能是电话打断或弱网导致）
                const graphQLError = error.graphQLErrors?.[0];
                const errorCode = graphQLError?.extensions?.code;
                const messageKey = graphQLError?.extensions?.messageKey;
                
                // 如果是内部错误，包装为网络异常
                if (errorCode === 'INTERNAL_ERROR' || messageKey === 'errors.system.internal') {
                    const networkError = new ApolloError({
                        errorMessage: t('errors.system.networkError'),
                        graphQLErrors: error.graphQLErrors,
                        networkError: {
                            name: 'NetworkError',
                            message: t('errors.system.networkError')
                        } as any,
                        extraInfo: error.extraInfo
                    });
                    (networkError as any).networkError = true;
                    throw networkError;
                }
            }
            
            // 其他错误直接抛出
            throw error;
        } finally {
            // 请求完成后移除pending标记
            pendingRequests.delete(orderId);
        }
    })();

    // 保存pending请求
    pendingRequests.set(orderId, request);
    return request;
};

/**
 * 带去重和缓存的获取门禁码函数（格式化后的数据）
 * 内部调用 getAccessCodesRawWithDedup 并格式化
 */
const getAccessCodesWithDedup = async (orderId: number): Promise<AccessCode[]> => {
    const rawCodes = await getAccessCodesRawWithDedup(orderId);
    return rawCodes.map(formatAccessCode);
};

interface UseAccessCodesOptions {
    orderId?: number;
    loading?: boolean;
    addressLat?: number;
    addressLon?: number;
}

interface UseAccessCodesReturn {
    accessCodes: AccessCode[];
    pendingAccessCodes: AccessCode[];
    validationDialogVisible: boolean;
    setValidationDialogVisible: (visible: boolean) => void;
    handleValidationComplete: (results: Array<{ codeId: number; status: 'valid' | 'invalid' | 'didntTry' }>) => Promise<void>;
    refreshAccessCodes: () => Promise<void>;
    checkPendingAccessCodes: () => Promise<boolean>; // 手动触发检查待验证码，返回是否有待验证码
    isLoadingAccessCodes: boolean; // 是否正在加载门禁码数据
    isFeatureEnabled: boolean; // 当前司机是否启用门禁码功能（来自 useAccessCodeFeature）
    isCheckingFeature: boolean; // 是否正在检查功能启用状态（来自 useAccessCodeFeature）
}

/**
 * 门禁码管理Hook
 * 负责：
 * 1. 获取门禁码列表（用于展示）
 * 2. 检查待验证门禁码并显示弹窗
 * 3. 处理有效性确认弹窗完成
 */
export const useAccessCodes = ({ orderId, loading, addressLat, addressLon }: UseAccessCodesOptions): UseAccessCodesReturn => {
    const [accessCodes, setAccessCodes] = useState<AccessCode[]>([]);
    const [pendingAccessCodes, setPendingAccessCodes] = useState<AccessCode[]>([]);
    const [validationDialogVisible, setValidationDialogVisible] = useState(false);
    const [isLoadingAccessCodes, setIsLoadingAccessCodes] = useState(false);

    // 使用 useAccessCodeFeature Hook 检查功能是否启用（避免重复逻辑）
    const { isEnabled: isFeatureEnabled, loading: isCheckingFeature } = useAccessCodeFeature();

    // 获取门禁码列表（用于展示）- 使用去重和缓存
    const fetchAccessCodes = async () => {
        if (!orderId) return;

        // 如果功能未启用，不获取门禁码
        if (!isFeatureEnabled) {
            setAccessCodes([]);
            return;
        }

        try {
            setIsLoadingAccessCodes(true);
            const formattedCodes = await getAccessCodesWithDedup(orderId);
            setAccessCodes(formattedCodes);
        } catch (error) {
            console.error('Failed to fetch access codes:', error);
            // 显示网络异常提示
            if (error instanceof ApolloError && (error.networkError || (error as any).networkError)) {
                Toast.show({
                    content: t('errors.system.networkError'),
                    position: 'bottom',
                    duration: 2000
                });
            }
        } finally {
            setIsLoadingAccessCodes(false);
        }
    };

    // 刷新门禁码列表
    const refreshAccessCodes = async () => {
        await fetchAccessCodes();
    };

    useEffect(() => {
        // 等待功能启用状态检查完成后再获取门禁码
        if (isCheckingFeature) return;
        
        if (orderId && !loading && isFeatureEnabled) {
            fetchAccessCodes();
        } else {
            // 如果 orderId 或 loading 状态变化，或功能未启用，重置 accessCodes（避免显示旧数据）
            if (!orderId || !isFeatureEnabled) {
                setAccessCodes([]);
            }
        }
    }, [orderId, loading, isFeatureEnabled, isCheckingFeature]);

    // 检查待验证门禁码并显示弹窗
    // 暂时禁用：本期先不考虑反馈弹框功能，但代码保留
    const checkPendingAccessCodes = async (): Promise<boolean> => {
        // 暂时禁用接口调用和弹窗显示
        return false;
        
        // 以下代码暂时注释，但保留
        // if (!orderId || loading || accessCodes.length === 0) return false;
        //
        // const userInfo = getUserInfo();
        // const driverAccountId = userInfo?.account?.id ? Number(userInfo.account.id) : null;
        // if (!driverAccountId) return false;
        //
        // try {
        //     const pendingCodes = await getPendingAccessCodesForDriver(orderId, driverAccountId);
        //
        //     if (pendingCodes.length > 0) {
        //         const formattedCodes = pendingCodes.map(formatAccessCode);
        //         setPendingAccessCodes(formattedCodes);
        //         setValidationDialogVisible(true);
        //         return true;
        //     }
        // } catch (error) {
        //     console.error('Failed to fetch pending access codes:', error);
        // }
        // return false;
    };

    // 处理有效性确认弹窗完成
    const handleValidationComplete = async (results: Array<{ codeId: number; status: 'valid' | 'invalid' | 'didntTry' }>) => {
        const userInfo = getUserInfo();
        const driverAccountId = userInfo?.account?.id ? Number(userInfo.account.id) : null;
        if (!driverAccountId) return;

        try {
            // 1. 标记所有code的弹窗已显示
            for (const result of results) {
                await markValidationPopupShown({
                    accessCodeId: result.codeId,
                    driverAccountId: driverAccountId
                });
            }

            // 2. 对于有效/无效反馈，提交反馈（不再进行位置校验）
            for (const result of results) {
                if (result.status === 'valid' || result.status === 'invalid') {
                    const code = pendingAccessCodes.find(c => c.id === result.codeId);
                    if (code) {
                        // 直接获取位置（如果获取不到就传null），不再进行位置校验
                        // 使用缓存，但不请求权限（反馈操作不需要强制获取位置）
                        const locationResult = await getCurrentLocation({
                            useCache: true,
                            requestPermissionIfDenied: false
                        });

                        // 当获取不到经纬度时，使用order地址的经纬度
                        const feedbackLatitude = locationResult.location?.latitude ?? addressLat ?? null;
                        const feedbackLongitude = locationResult.location?.longitude ?? addressLon ?? null;

                        // 提交反馈（从弹窗反馈，计入popupShownAt）
                        await submitFeedback({
                            driverAccountId: driverAccountId,
                            isValidFeedback: result.status === 'valid',
                            latitude: feedbackLatitude,
                            longitude: feedbackLongitude,
                            accessCode: {
                                id: code.id,
                                codeType: code.type,
                                accessCode: code.code,
                                status: code.status
                            },
                            fromPopup: true // 从弹窗反馈，计入popupShownAt
                        });
                    }
                }
            }

            // 3. 刷新accessCodes列表
            await refreshAccessCodes();
        } catch (error) {
            console.error('Failed to handle validation complete:', error);
            Toast.show({
                content: t('pages.delivery.orderDetail.validationFailed'),
                position: 'bottom'
            });
        }
    };

    return {
        accessCodes,
        pendingAccessCodes,
        validationDialogVisible,
        setValidationDialogVisible,
        handleValidationComplete,
        refreshAccessCodes,
        checkPendingAccessCodes,
        isLoadingAccessCodes,
        isFeatureEnabled,
        isCheckingFeature
    };
};

